import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { Empty, Spin, Table } from 'antd-v5';
import { listInquiries } from '../../../../../api/vendors';
import { getAllMerchantTags } from '../../../../../services/clients';
import {
  transformInquiry,
  isTerminalStatus,
  resolveInquirySort,
  flipSortOrder,
  columnSortOrder,
  DEFAULT_INQUIRY_SORT,
  INITIAL_INQUIRY_FILTERS,
  deserializeInquiryFilters,
} from '../../../../../components/Routes/Rolex/utils';
import useVendorByKey from '../../../../../hooks/query/useVendorByKey';
import useDebounce from '../../../../../hooks/useDebounce';
import useSessionState from '../../../../../hooks/useSessionState';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import AddRolexInquiryModal from '../../../../Rolex/AddRolexInquiryModal';
import InquiryFilters from '../../../../../components/Routes/Rolex/components/InquiryFilters';

const AllRolexInquiries = ({ auth }) => {
  const history = useHistory();
  const { vendorId: rolexVendorId } = useVendorByKey('rolex');
  // Filters, sort and paging persist for the tab so they survive drilling into a client and coming back
  const storageKey = field => (auth.userId ? `all-rolex-inquiries:${auth.userId}:${field}` : null);
  const [filters, setFilters] = useSessionState(storageKey('filters'), INITIAL_INQUIRY_FILTERS, {
    deserialize: deserializeInquiryFilters,
  });
  const [page, setPage] = useSessionState(storageKey('page'), 1);
  const [pageSize, setPageSize] = useSessionState(storageKey('pageSize'), 25);
  const [sortBy, setSortBy] = useSessionState(storageKey('sortBy'), DEFAULT_INQUIRY_SORT.sortBy);
  const [sortOrder, setSortOrder] = useSessionState(storageKey('sortOrder'), DEFAULT_INQUIRY_SORT.sortOrder);
  const [rawData, setRawData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingInquiry, setEditingInquiry] = useState(null);
  const [tagLookup, setTagLookup] = useState([]);

  useEffect(() => {
    getAllMerchantTags({ loadingStatusOff: true })
      .then(merchantTags => setTagLookup(merchantTags || []))
      .catch(() => {});
  }, []);

  // Debounce text search; bypass when cleared so we don't fire a stale fetch
  const debouncedClientSearch = useDebounce(filters.clientSearch, 500);
  const effectiveClientSearch = filters.clientSearch ? debouncedClientSearch : '';

  // Wrapper that updates filters and resets page atomically so only one fetch fires
  const updateFilters = useCallback(updater => {
    setFilters(prev => (typeof updater === 'function' ? updater(prev) : updater));
    setPage(1);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const query = {
        vendorKey: 'rolex',
        page,
        pageSize,
        sortBy,
        sortOrder,
      };
      if (auth.role === 'ASSOCIATE') {
        query.associateId = auth.userId;
      } else if (filters.associate?.length) {
        query.associateId = filters.associate.map(Number);
      }
      if (effectiveClientSearch) query.clientSearch = effectiveClientSearch;
      if (filters.status?.length) query.statuses = filters.status;
      if (filters.tags?.length) query.tag = filters.tags;
      if (filters.collections?.length) query.collections = filters.collections;
      if (filters.families?.length) query.families = filters.families;
      if (filters.materials?.length) query.materials = filters.materials;
      if (filters.stores?.length) query.storeId = filters.stores.map(Number);
      if (filters.dateRange?.[0]) query.startDate = filters.dateRange[0].format('YYYYMMDD');
      if (filters.dateRange?.[1]) query.endDate = filters.dateRange[1].format('YYYYMMDD');

      const response = await listInquiries(query);
      setRawData(response?.data || []);
      setTotal(response?.total || (response?.data || []).length);
    } catch {
      setRawData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    auth?.userId,
    auth?.role,
    effectiveClientSearch,
    filters.associate,
    filters.status,
    filters.tags,
    filters.collections,
    filters.families,
    filters.materials,
    filters.stores,
    filters.dateRange,
    page,
    pageSize,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply tag/store name resolution at render time so changes to tagLookup
  // don't trigger a refetch
  const data = useMemo(() => {
    const stores = auth?.stores || [];
    return rawData.map(item => transformInquiry(item, { stores, tags: tagLookup }));
  }, [rawData, auth?.stores, tagLookup]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'clientName',
      key: 'clientName',
      sorter: true,
      sortOrder: columnSortOrder('clientName', sortBy, sortOrder),
      render: (text, record) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span
          style={{ color: '#1677ff', cursor: 'pointer' }}
          data-testid="inquiries__table--name"
          onClick={() => history.push(`/Clients?client=${record.clientId}&clientTab=rolex`)}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Inquiry Date',
      dataIndex: 'inquiryDate',
      key: 'inquiryDate',
      sorter: true,
      sortOrder: columnSortOrder('inquiryDate', sortBy, sortOrder),
      render: (text, record) => (
        <div>
          <div style={{ fontSize: 14 }}>{text}</div>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>{record.durationMonths}</div>
        </div>
      ),
    },
    {
      title: 'Last Inbound',
      dataIndex: 'lastInbound',
      key: 'lastInbound',
      render: (text, record) => (
        <div>
          <div style={{ fontSize: 14 }}>{text}</div>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>{record.lastInboundDuration}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: value => {
        const label = { WAITING: 'Waiting', ORDERED: 'Ordered', CANCELED: 'Canceled', FULFILLED: 'Fulfilled' };
        return label[value] || value;
      },
    },
    {
      title: 'Associate',
      dataIndex: 'associateName',
      key: 'associateName',
    },
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      render: (text, record) => {
        const canNavigate = !!(rolexVendorId && record.productVendorItemId);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {record.productImage && (
              <img
                src={record.productImage}
                alt={record.productMpn}
                style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }}
              />
            )}
            <div>
              {canNavigate ? (
                <Link
                  to={`/Products?vendorId=${rolexVendorId}&productId=${record.productVendorItemId}`}
                  style={{ color: '#1677ff' }}
                >
                  {text}
                </Link>
              ) : (
                <div>{text}</div>
              )}
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>
                {record.productMpn}
                {record.productCount > 1 && (
                  <span style={{ color: 'rgba(0,0,0,0.35)', fontStyle: 'italic' }}>
                    {' '}
                    +{record.productCount - 1} more
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'edit',
      width: 80,
      render: (_, record) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span
          style={{ color: '#1677ff', cursor: 'pointer' }}
          data-testid="inquiries__table--edit-button"
          onClick={() => setEditingInquiry(record)}
        >
          {isTerminalStatus(record.status) ? 'View' : 'Edit'}
        </span>
      ),
    },
  ];

  const handleTableChange = useCallback((pagination, _filters, sorter, extra) => {
    if (extra.action === 'sort') {
      const order = Array.isArray(sorter) ? sorter[0]?.order : sorter?.order;
      const columnKey = Array.isArray(sorter) ? sorter[0]?.columnKey : sorter?.columnKey;
      if (order) {
        const next = resolveInquirySort(columnKey, order);
        setSortBy(next.sortBy);
        setSortOrder(next.sortOrder);
      } else {
        // antd's 3rd click clears the sort; a server-side sort always has an
        // active column, so flip its direction instead (lets the initial
        // Inquiry Date DESC toggle to ASC).
        setSortOrder(flipSortOrder);
      }
      setPage(1);
    } else if (extra.action === 'paginate') {
      setPage(pagination.current);
      setPageSize(pagination.pageSize);
    }
  }, []);

  return (
    <StandardLayoutV2
      title="Rolex Inquiries"
      breadcrumb={[{ path: '/', title: 'Today' }, { title: 'Rolex Inquiries' }]}
      classProps="flex-col-left"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingTop: '0px' }}
    >
      <InquiryFilters
        filters={filters}
        setFilters={updateFilters}
        onReset={() => updateFilters(INITIAL_INQUIRY_FILTERS)}
      />

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          size="middle"
          onRow={() => ({ 'data-testid': 'inquiries__table--row' })}
          onChange={handleTableChange}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: { showSearch: false },
            showTotal: t => `Total ${t} items`,
          }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Rolex inquiries found" /> }}
        />
      )}

      <AddRolexInquiryModal
        open={!!editingInquiry}
        onCancel={() => setEditingInquiry(null)}
        onSave={() => {
          setEditingInquiry(null);
          fetchData();
        }}
        inquiry={editingInquiry}
        vendorId={rolexVendorId}
      />
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AllRolexInquiries);
